import { DISCOUNT_IDS } from '@constants/apiConstants';
import { QuoteIneligibleErrorMessages } from '@constants/apiErrors';
import { CURRENCY_CODE } from '@constants/app';
import {
  BundleBasketFragment,
  CashbackCalculatorDetailsFragment,
  ProductType,
  Quote,
  QuoteStatus,
  QuoteUnavailabilityReasonType,
  Status,
} from '@generated/gql/graphql';

const isAvailable = ({ status, reasons }: QuoteStatus) => {
  if (status === Status.Available) {
    return true;
  }

  const reasonsCopy = Array.from(reasons ?? []);

  if (reasonsCopy.length === 0) {
    throw new Error('parseCashback: unexpected state in CBC status check');
  }

  const accountHolderReasonIndex = reasonsCopy.findIndex(
    (reason) =>
      reason.type === QuoteUnavailabilityReasonType.Incomplete &&
      reason.description ===
        QuoteIneligibleErrorMessages.atLeastOneAccountHolderRequired
  );

  const anotherServiceRequiredReasonIndex = reasonsCopy.findIndex(
    (reason) =>
      reason.type === QuoteUnavailabilityReasonType.Ineligible &&
      reason.description ===
        QuoteIneligibleErrorMessages.cashbackCardMustBeBundledWithAnotherService
  );

  if (
    accountHolderReasonIndex === -1 &&
    anotherServiceRequiredReasonIndex === -1
  ) {
    return false;
  }

  if (accountHolderReasonIndex !== -1) {
    reasonsCopy.splice(accountHolderReasonIndex, 1);
  }

  if (anotherServiceRequiredReasonIndex !== -1) {
    reasonsCopy.splice(anotherServiceRequiredReasonIndex, 1);
  }

  return reasonsCopy.length === 0;
};

const formatCashback = (basket: BundleBasketFragment) => {
  const quotes = basket.quotes as Quote[];

  const cbcQuote = quotes.find(
    (quote) => quote.product.type === ProductType.CashbackCard
  );

  let discount = null;

  if (!cbcQuote) {
    return {
      available: false,
      selected: false,
      monthlyCost: {
        currency: CURRENCY_CODE,
        value: '0',
      },
      discount,
    };
  }

  const { selected, status, totalMonthlyCost, discounts } = cbcQuote;

  const hasThreeMonthsFree = discounts.find(
    ({ id }) => id === DISCOUNT_IDS.CBC_THREE_MONTHS_FREE_DISCOUNT
  );

  if (hasThreeMonthsFree && hasThreeMonthsFree.qualified) {
    discount = {
      price: {
        value: '0.00',
        currency: CURRENCY_CODE,
      },
      durationMonths: 3,
    };
  }

  return {
    available: isAvailable(status),
    selected,
    monthlyCost: totalMonthlyCost,
    discount,
  };
};

const getCashbackSavingsValue = (
  cashbackDetails: Nullable<CashbackCalculatorDetailsFragment>
) => {
  const { estimatedCashbackValue, useCashbackValueInComms } =
    cashbackDetails ?? {};

  if (
    !useCashbackValueInComms ||
    !estimatedCashbackValue ||
    Number(estimatedCashbackValue) === 0
  ) {
    return null;
  }

  return estimatedCashbackValue;
};

export { formatCashback, getCashbackSavingsValue };
